import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface KnowledgeSourceRecord {
  id: string;
  tenantId: string;
  sourceType: string;
  configJson: string;
  status: string;
  lastIngestedUtc?: string | null;
  accessScopeJson: string;
}

export interface KnowledgeDocumentRecord {
  id: string;
  sourceId: string;
  title: string;
  uri: string;
  version: number;
  status: string;
  effectiveFromUtc?: string | null;
  effectiveToUtc?: string | null;
  createdAtUtc: string;
}

export interface KnowledgeChunkRecord {
  id: string;
  chunkIndex: number;
  text: string;
  sensitivityLabel: string;
  tokenCount: number;
}

export interface KnowledgeRetrievalLogRecord {
  id: string;
  query: string;
  topK: number;
  consumerModule: string;
  requestedByUserId?: string | null;
  requestedAtUtc: string;
  chunkIds: string[];
}

export interface KnowledgeSourceHealthRecord {
  sourceId: string;
  status: string;
  lastIngestedUtc?: string | null;
  isStale: boolean;
  activeDocumentCount: number;
  chunkCount: number;
}

export interface KnowledgeRetrievalResult {
  query: string;
  topK: number;
  items: Array<{
    chunkId: string;
    score: number;
    text: string;
    citation: {
      sourceId: string;
      documentId: string;
      documentTitle: string;
      documentUri?: string | null;
      chunkIndex: number;
      documentVersion: number;
    };
    metadata: Record<string, string>;
  }>;
}

@Injectable({ providedIn: 'root' })
export class KnowledgeApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/knowledge`;

  listSources(): Observable<KnowledgeSourceRecord[]> {
    return this.http.get<KnowledgeSourceRecord[]>(`${this.base}/sources`);
  }

  getSource(sourceId: string): Observable<KnowledgeSourceRecord> {
    return this.http.get<KnowledgeSourceRecord>(`${this.base}/sources/${sourceId}`);
  }

  createSource(payload: { sourceType: string; configJson: string; accessScopeJson: string }): Observable<KnowledgeSourceRecord> {
    return this.http.post<KnowledgeSourceRecord>(`${this.base}/sources`, payload);
  }

  updateSource(sourceId: string, payload: { sourceType: string; configJson: string; accessScopeJson: string }): Observable<KnowledgeSourceRecord> {
    return this.http.put<KnowledgeSourceRecord>(`${this.base}/sources/${sourceId}`, payload);
  }

  activateSource(sourceId: string): Observable<KnowledgeSourceRecord> {
    return this.http.post<KnowledgeSourceRecord>(`${this.base}/sources/${sourceId}/activate`, {});
  }

  deactivateSource(sourceId: string): Observable<KnowledgeSourceRecord> {
    return this.http.post<KnowledgeSourceRecord>(`${this.base}/sources/${sourceId}/deactivate`, {});
  }

  ingestDocument(sourceId: string, payload: {
    title: string;
    uri: string;
    content: string;
    effectiveFromUtc?: string | null;
    effectiveToUtc?: string | null;
  }): Observable<{ sourceId: string; documentId: string; changed: boolean; createdChunkCount: number }> {
    return this.http.post<{ sourceId: string; documentId: string; changed: boolean; createdChunkCount: number }>(
      `${this.base}/sources/${sourceId}/ingest`,
      payload
    );
  }

  listDocuments(sourceId?: string): Observable<KnowledgeDocumentRecord[]> {
    const query = sourceId ? `?sourceId=${encodeURIComponent(sourceId)}` : '';
    return this.http.get<KnowledgeDocumentRecord[]>(`${this.base}/documents${query}`);
  }

  listChunks(documentId: string): Observable<KnowledgeChunkRecord[]> {
    return this.http.get<KnowledgeChunkRecord[]>(`${this.base}/documents/${documentId}/chunks`);
  }

  listRetrievalLogs(take = 25): Observable<KnowledgeRetrievalLogRecord[]> {
    return this.http.get<KnowledgeRetrievalLogRecord[]>(`${this.base}/retrieval-logs?take=${take}`);
  }

  getSourceHealth(sourceId: string): Observable<KnowledgeSourceHealthRecord> {
    return this.http.get<KnowledgeSourceHealthRecord>(`${this.base}/sources/${sourceId}/health`);
  }

  reingestSource(sourceId: string): Observable<{ sourceId: string; processedDocumentCount: number; reEmbeddedChunkCount: number }> {
    return this.http.post<{ sourceId: string; processedDocumentCount: number; reEmbeddedChunkCount: number }>(
      `${this.base}/sources/${sourceId}/reingest`,
      {}
    );
  }

  retrieve(payload: { query: string; topK: number; consumerModule?: string; filters?: Record<string, string> }): Observable<KnowledgeRetrievalResult> {
    return this.http.post<KnowledgeRetrievalResult>(`${this.base}/retrieve`, payload);
  }
}
